import { api } from './client'

export type SalesOrderStatus =
    | 'new'
    | 'awaiting_packaging'
    | 'confirmed'
    | 'stock_out'
    | 'ready_to_deliver'
    | 'in_delivery'
    | 'delivered'
    | 'delivery_failed'
    | 'returned'
    | 'cancelled'

export type PaymentStatus = 'unpaid' | 'partial' | 'paid'

export type PrintJobStatus = 'queued' | 'printing' | 'printed' | 'failed'

export interface PrintJob {
    id: string
    sales_order_id: string
    doc_type: string
    status: PrintJobStatus
    attempts: number
    last_error?: string | null
    printed_at?: string | null
    created_at: string
    updated_at: string
}

export interface SalesOrderItem {
    id: string
    sales_order_id: string
    product_id: string
    product_name: string
    product_sku?: string
    // Đơn vị bán (gói/kg/hộp) — BE gán từ product khi tạo item. Optional để
    // không vỡ order cũ chưa có unit.
    unit?: string
    quantity: number
    unit_price: number
    discount_percent: number
    subtotal: number
    /** Dòng đã tách giá vật tư khỏi giá SP (SP price_includes_material). */
    material_price_split?: boolean
    weight_value?: number
    weight_unit?: string
}

export interface SalesOrder {
    id: string
    order_code: string
    source_channel: string
    customer_id: string
    customer_name: string
    customer_phone: string
    shipping_address: string
    shipping_province?: string
    shipping_province_code?: string
    shipping_ward?: string
    shipping_ward_code?: string
    status: SalesOrderStatus
    total_amount: number
    vat_percent?: number | null
    vat_amount?: number | null
    payment_status: PaymentStatus
    paid_amount: number
    created_by_id: string
    created_by_name?: string
    payment_method?: string
    settlement_intent?: 'cash' | 'bank_transfer' | 'credit'
    discount_percent?: number
    driver_assignee_id?: string
    delivered_at?: string
    delivery_note?: string
    product_notes?: string
    /** Mã đơn NV tự điền để đối chiếu với sổ ghi tay. Free-text, có thể trùng. */
    internal_order_code?: string
    shipping_carrier_id?: string
    shipping_carrier_name?: string
    /**
     * Mirror của shipping_carriers.is_external join-resolved trên order.
     * FE dùng để render các button carrier-mark-* (kế toán reconcile) khi
     * đơn giao bởi carrier ngoài.
     */
    shipping_carrier_is_external?: boolean
    shipping_fee?: number
    shipping_fee_payer?: 'customer' | 'company'
    collect_cod?: boolean
    sla_deadline_at?: string
    /** Thời điểm bắt đầu bắn cảnh báo "sắp trễ" (deadline - warning_minutes). */
    sla_warning_at?: string
    sla_breached: boolean
    delivery_trip_id?: string
    delivery_vehicle?: DeliveryVehicle
    pod_photo_url?: string
    pod_photo_captured_at?: string
    created_at: string
    updated_at: string
    items?: SalesOrderItem[]
}

/** Vehicle of the order's delivery trip, joined on the detail fetch. */
export interface DeliveryVehicle {
    id: string
    plate_number: string
    name: string
    gps_provider?: string
    gps_imei?: string
}

export interface SalesOrderStatusLog {
    id: string
    sales_order_id: string
    from_status?: string
    to_status: string
    changed_by_id: string
    changed_by_name: string
    note?: string
    created_at: string
}

export interface SalesOrderPayment {
    id: string
    sales_order_id: string
    amount: number
    payment_method: string
    reference?: string
    notes?: string
    created_by_name: string
    collected_by_driver_id?: string
    collected_by_driver_name?: string
    created_at: string
}

export interface SalesOrderNotification {
    id: string
    sales_order_id?: string
    receiver_user_id: string
    type: string
    payload: unknown
    is_read: boolean
    created_at: string
}

export interface CreateOrderItemInput {
    product_id: string
    quantity: number
    unit_price: number
    discount_percent?: number
    /** Dòng đã tách giá vật tư (lưu trạng thái nút Tách/Gộp). */
    material_price_split?: boolean
}

// Phase 4 — packaging draft item gửi kèm khi tạo đơn.
// source: 'bom_autofill' (hệ thống gợi ý theo BOM) | 'manual' (sale tự thêm).
export interface CreatePackagingItemInput {
    packaging_material_id: string
    quantity: number
    source?: 'bom_autofill' | 'manual'
    /** Đơn giá vật tư (giá vật tư). 0 = không tính tiền. */
    unit_price?: number
    /** Vật tư đóng gói SP: chỉ tính tiền, KHÔNG trừ kho. */
    charge_only?: boolean
    /** Chiết khấu dòng (%) của vật tư (người bán nhập). */
    discount_percent?: number
    /** Sản phẩm mà dòng vật tư charge_only thuộc về (để hiển thị inline). */
    product_id?: string
    /** Vị trí dòng đơn (0-based, theo mảng items) mà vật tư charge_only thuộc về.
     *  BE map index → order_item_id. Bỏ trống cho vật tư đính-kèm/thêm tay. */
    order_item_index?: number
}

export interface CreateOrderRequest {
    source_channel: string
    customer_id: string
    customer_name: string
    customer_phone: string
    shipping_address: string
    shipping_province?: string
    shipping_province_code?: string
    shipping_ward?: string
    shipping_ward_code?: string
    product_notes?: string
    /** Mã đơn NV tự điền để đối chiếu với sổ ghi tay. Free-text, có thể trùng. */
    internal_order_code?: string
    shipping_carrier_id?: string
    shipping_fee?: number
    shipping_fee_payer?: 'customer' | 'company'
    // Whether to collect payment from the customer on delivery (COD).
    // Enforced at ConfirmDelivery: when false, goods-cash settlement is blocked.
    // Optional so existing callers that predate this field are unaffected; BE
    // defaults to false when absent.
    collect_cod?: boolean
    discount_percent?: number
    // Thuế GTGT người bán chỉnh trên form (2 chiều % ↔ tiền). Gửi cả hai để BE
    // snapshot đúng số tiền (tránh lệch làm tròn). Bỏ trống → BE dùng VAT global.
    vat_percent?: number
    vat_amount?: number
    items?: CreateOrderItemInput[]
    // Phase 4 — vật tư bao bì sale đã chốt cho đơn (autofill từ BOM, có thể
    // edit tự do). BE lưu vào sales_order_packaging_draft, Confirm dùng draft.
    packaging_items?: CreatePackagingItemInput[]
    // Khi kho thiếu hàng, true cho phép BE tạo đơn ở AwaitingPackaging (chờ đội
    // đóng gói bổ sung) thay vì reject 409. Đủ hàng thì auto-confirm bình thường.
    // FE dùng flag này để retry sau khi cashier confirm popup "Kho thiếu, tạo
    // đơn chờ đóng gói?".
    allow_awaiting?: boolean
}

// Backend trả về khi POST /sales-orders 409 INSUFFICIENT_STOCK: liệt kê SP
// thiếu để FE render bảng popup "Tạo đơn chờ đóng gói?".
export interface StockShortfallItem {
    product_id: string
    product_name: string
    available: number
    requested: number
}

// Response từ GET /sales-orders/:id/stock-check cho đơn AwaitingPackaging.
export interface StockCheckResponse {
    ready: boolean
    shortfalls: StockShortfallItem[]
}

// Một dòng trong popup gán tài xế của admin.
export interface AssignableDriver {
    id: string
    /** Phân biệt hai tài khoản trùng `full_name`. */
    username: string
    full_name: string
    active_order_count: number
}

export interface QuickSaleRequest extends CreateOrderRequest {
    payment_method: 'cash' | 'bank_transfer'
    reference?: string
    display_user_id?: string
}

export interface UpdateOrderRequest {
    customer_name?: string
    customer_phone?: string
    shipping_address?: string
    shipping_province?: string
    shipping_province_code?: string
    shipping_ward?: string
    shipping_ward_code?: string
    product_notes?: string | null
    internal_order_code?: string | null
    shipping_carrier_id?: string
    shipping_fee?: number
    shipping_fee_payer?: 'customer' | 'company'
    collect_cod?: boolean
    vat_percent?: number
    vat_amount?: number
    items?: CreateOrderItemInput[]
    // Phase 4 — sale sửa lại draft vật tư kèm đơn. undefined = giữ draft
    // hiện tại; [] = xoá draft; array có item = thay toàn bộ.
    packaging_items?: CreatePackagingItemInput[]
}

export interface ListOrdersParams {
    page?: number
    per_page?: number
    status?: SalesOrderStatus
    payment_status?: 'unpaid' | 'partial' | 'paid'
    source_channel?: string
    settlement_intent?: 'cash' | 'bank_transfer' | 'credit'
    customer_id?: string
    search?: string
    date_from?: string
    date_to?: string
    // payable=true chỉ lấy đơn còn nhận thanh toán (chưa huỷ + còn phải thu > 0)
    // — dùng cho ô chọn đơn ở màn đối soát.
    payable?: boolean
}

export interface RecordPaymentInput {
    amount: number
    method: string
    reference?: string
}

export interface ConfirmOrderPayload {
    note?: string
    // Phase 4 — vật tư bao bì user submit. Thường để rỗng và BE đọc draft
    // (đã lưu lúc Create). Chỉ truyền nếu muốn override draft tại Confirm step.
    packaging_items?: { packaging_material_id: string; quantity: number }[]
    // Cho phép thiếu stock vật tư (warning bypass). Mọi shortage giờ chỉ là warning.
    accept_packaging_shortage?: boolean
}

// Vật tư đã trừ ở Confirm step (group theo lot). FE ConfirmReturn popup
// prefill list + trang chi tiết đơn hiển thị.
export interface PackagingDeductedItem {
    packaging_material_id: string
    material_sku: string
    material_name: string
    material_unit: string
    warehouse_id: string
    packaging_lot_id: string
    lot_code: string
    deducted_qty: number
}

// SP đã trừ kho ở Confirm step (group theo lot). Mirror PackagingDeductedItem.
export interface InventoryDeductedItem {
    product_id: string
    product_sku: string
    product_name: string
    inventory_lot_id: string
    lot_code: string
    deducted_qty: number
}

// Phase 4 — packaging draft response (cho FE Confirm flow đọc trước khi confirm).
export interface PackagingDraftItem {
    id: string
    sales_order_id: string
    packaging_material_id: string
    quantity: number
    source: 'bom_autofill' | 'manual'
    /** Đơn giá vật tư (giá vật tư) snapshot lúc lên đơn. */
    unit_price: number
    /** Vật tư đóng gói SP: chỉ tính tiền, KHÔNG trừ kho. */
    charge_only: boolean
    /** Chiết khấu dòng (%) của vật tư. */
    discount_percent: number
    /** Sản phẩm mà dòng vật tư charge_only thuộc về. */
    product_id: string
    /** Dòng đơn (order_item) mà vật tư charge_only thuộc về. null cho order-level. */
    order_item_id?: string | null
    material_sku: string
    material_name: string
    material_unit: string
    /** SUM(packaging_lots.qty_remaining) cho material — FE so qty để detect shortage. */
    available: number
    created_at: string
    updated_at: string
}

export type StockOutNoteStatus = 'pending' | 'confirmed' | 'cancelled'

export interface StockOutNote {
    id: string
    code: string
    sales_order_id: string
    warehouse_id: string
    status: StockOutNoteStatus
    notes?: string
    /** Packing photo-evidence URLs attached at confirmation. */
    photo_paths: string[]
    created_by_id: string
    confirmed_by_id?: string
    created_at: string
    confirmed_at?: string
}

export interface ConfirmStockOutNoteRequest {
    note?: string
    /** Upload URLs of packing evidence photos (at least one required). */
    photo_paths: string[]
}

export interface DriverDeliverySummary {
    delivered: number
    in_delivery: number
    on_time: number
    sla_breached: number
}

export interface MyDeliverySummaryParams {
    date_from?: string
    date_to?: string
}

export interface PaymentQRData {
    qr_url: string
    bank_account: string
    bank_name: string
    bank_holder: string
    amount: number
    content: string
}

export const salesOrderApi = {
    list: (params?: ListOrdersParams) =>
        api.get<SalesOrder[]>('/sales-orders', { params }),
    getById: (id: string) => api.get<SalesOrder>(`/sales-orders/${id}`),
    /**
     * Xuất toàn bộ đơn tạo trong ngày ra Excel. Trả nguyên AxiosResponse để
     * caller đọc Content-Disposition lấy tên file server đặt.
     */
    exportDailyExcel: (date: string, paidOnly = true) =>
        api.getBlob('/sales-orders/export/daily-excel', {
            params: { date, paid_only: paidOnly },
        }),
    /**
     * Resolve a free-form code (DH... order code or PXK-... stock-out code)
     * to the underlying sales order. Used by the QR/barcode scanner so users
     * can type the printed code on a slip if the camera fails.
     */
    lookupByCode: (code: string) =>
        api.get<SalesOrder>(`/sales-orders/lookup`, {
            params: { code },
        }),
    /**
     * Sửa mã đơn nội bộ. Cho phép ở MỌI trạng thái đơn (kể cả đã giao / đã huỷ) vì
     * đây là mã đối chiếu với hệ thống ngoài, có thể được cấp/đổi sau khi đơn đã đi
     * hết vòng đời. Đổi lại: BE chỉ cho ĐÚNG NGƯỜI TẠO ĐƠN sửa (403 nếu không phải).
     * Gửi chuỗi rỗng = xoá mã.
     */
    updateInternalCode: (id: string, internalOrderCode: string) =>
        api.patch<SalesOrder>(`/sales-orders/${id}/internal-code`, {
            internal_order_code: internalOrderCode,
        }),
    create: (data: CreateOrderRequest) =>
        api.post<SalesOrder>('/sales-orders', data),
    quickCreate: (data: QuickSaleRequest) =>
        api.post<SalesOrder>('/sales-orders/quick', data),
    /**
     * Create a deliver-later (giao sau) order from the POS. Goes through
     * create → confirm → stock-out with no payment (COD); the order lands at
     * stock_out + unpaid so a driver can pick it up immediately.
     */
    createDelivery: (data: CreateOrderRequest) =>
        api.post<SalesOrder>('/sales-orders/delivery', data),
    displaySync: (data: {
        display_user_id: string
        type: 'cart' | 'qr' | 'checkout' | 'clear'
        data?: unknown
    }) => api.post<{ success: boolean }>('/customer-display/sync', data),
    update: (id: string, data: UpdateOrderRequest) =>
        api.put<SalesOrder>(`/sales-orders/${id}`, data),

    // Workflow actions
    /**
     * Kiểm tra sẵn sàng Confirm cho đơn AwaitingPackaging. Trả ready + shortfalls[].
     * Read-only, có thể poll F5. Backend reject 400 nếu đơn không ở trạng thái
     * awaiting_packaging.
     */
    checkStock: (id: string) =>
        api.get<StockCheckResponse>(`/sales-orders/${id}/stock-check`),
    confirm: (id: string, data?: ConfirmOrderPayload) =>
        api.post<SalesOrder>(`/sales-orders/${id}/confirm`, data ?? {}),
    stockOut: (id: string, data?: { notes?: string }) =>
        api.post<SalesOrder>(`/sales-orders/${id}/stock-out`, data ?? {}),
    assignDriver: (id: string, data: { driver_id: string }) =>
        api.post<SalesOrder>(`/sales-orders/${id}/assign-driver`, data),
    /**
     * Danh sách tài xế cho popup gán đơn của admin. `active_order_count` = số
     * đơn tài xế đang giữ (ready_to_deliver / in_delivery); 0 = đang rảnh.
     * Tài xế bận vẫn gán được — một chuyến chở nhiều đơn — nên đây là thông
     * tin tham khảo, không phải bộ lọc.
     */
    assignableDrivers: () =>
        api.get<AssignableDriver[]>('/sales-orders/assignable-drivers'),
    pickupOrder: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(`/sales-orders/${id}/pickup-order`, data ?? {}),
    startDelivery: (
        id: string,
        data: {
            note?: string
            vehicle_id?: string
        },
    ) => api.post<SalesOrder>(`/sales-orders/${id}/start-delivery`, data),
    confirmDelivery: (
        id: string,
        data: {
            note?: string
            // Settlement on delivery: 'cash' records driver-collected cash (feeds
            // the cash ledger), 'bank_transfer' defers to the webhook, 'credit'
            // leaves the debt. Omitted records nothing.
            method?: 'cash' | 'bank_transfer' | 'credit'
            cash_amount?: number
            // Driver also collected the order's carrier fee in cash
            // ("thu cước hộ"); amount comes from the order's shipping_fee.
            collect_shipping_fee?: boolean
        },
    ) => api.post<SalesOrder>(`/sales-orders/${id}/confirm-delivery`, data),
    failDelivery: (id: string, data: { note: string }) =>
        api.post<SalesOrder>(`/sales-orders/${id}/fail-delivery`, data),
    confirmReturn: (
        id: string,
        data?: {
            note?: string
            inventory_restore_items?: {
                inventory_lot_id: string
                quantity: number
            }[]
            packaging_restore_items?: {
                packaging_lot_id: string
                quantity: number
            }[]
        },
    ) => api.post<SalesOrder>(`/sales-orders/${id}/confirm-return`, data ?? {}),
    cancel: (id: string, data: { note: string }) =>
        api.post<SalesOrder>(`/sales-orders/${id}/cancel`, data),

    // Carrier reconcile — kế toán mark delivery status thay driver cho đơn
    // giao bởi carrier bên ngoài (shipping_carrier_is_external=true). Payload
    // tối giản: chỉ note, không lat/lng, không vehicle, không COD method.
    carrierMarkReadyToDeliver: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(
            `/sales-orders/${id}/carrier-mark-ready-to-deliver`,
            data ?? {},
        ),
    carrierMarkInDelivery: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(
            `/sales-orders/${id}/carrier-mark-in-delivery`,
            data ?? {},
        ),
    carrierMarkDelivered: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(
            `/sales-orders/${id}/carrier-mark-delivered`,
            data ?? {},
        ),
    carrierMarkDeliveryFailed: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(
            `/sales-orders/${id}/carrier-mark-delivery-failed`,
            data ?? {},
        ),
    carrierMarkReturned: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(
            `/sales-orders/${id}/carrier-mark-returned`,
            data ?? {},
        ),

    // Payments
    recordPayments: (id: string, data: { payments: RecordPaymentInput[] }) =>
        api.post<SalesOrderPayment[]>(`/sales-orders/${id}/payments`, data),
    listPayments: (id: string) =>
        api.get<SalesOrderPayment[]>(`/sales-orders/${id}/payments`),

    // Timeline
    timeline: (id: string) =>
        api.get<SalesOrderStatusLog[]>(`/sales-orders/${id}/timeline`),

    // Phase 4 — packaging draft của 1 đơn (sale đã chốt lúc tạo). FE Confirm
    // flow đọc để hiển thị + dùng làm packaging_items.
    getPackagingDraft: (id: string) =>
        api.get<PackagingDraftItem[]>(`/sales-orders/${id}/packaging-draft`),
    getPackagingDeducted: (id: string) =>
        api.get<PackagingDeductedItem[]>(
            `/sales-orders/${id}/packaging-deducted`,
        ),
    getInventoryDeducted: (id: string) =>
        api.get<InventoryDeductedItem[]>(
            `/sales-orders/${id}/inventory-deducted`,
        ),

    // Notifications
    listNotifications: (params?: { page?: number; per_page?: number }) =>
        api.get<SalesOrderNotification[]>('/notifications', { params }),
    markNotificationRead: (id: string) =>
        api.put('/notifications/' + id + '/read'),
    markAllNotificationsRead: () => api.put('/notifications/read-all'),
    unreadNotificationCount: () =>
        api.get<{ count: number }>('/notifications/unread-count'),

    // Push subscription
    subscribePush: (data: { endpoint: string; p256dh: string; auth: string }) =>
        api.post('/push/subscribe', data),
    unsubscribePush: (endpoint: string) =>
        api.post('/push/unsubscribe', { endpoint }),

    // Payment QR
    getPaymentQR: (id: string) =>
        api.get<PaymentQRData>(`/sales-orders/${id}/payment-qr`),

    // Driver self-scoped delivery performance (no reports.read required)
    myDeliverySummary: (params?: MyDeliverySummaryParams) =>
        api.get<DriverDeliverySummary>('/sales-orders/my-delivery-summary', {
            params,
        }),

    // Self-scoped revenue summary for the authenticated sales rep.
    // Gated sales_orders.read — does not require reports.read.
    myOrdersSummary: (params?: MyDeliverySummaryParams) =>
        api.get<MyRevenueSummary>('/sales-orders/summary/mine', { params }),

    // Self-scoped revenue time-series for the authenticated sales rep.
    // Gated sales_orders.read — does not require reports.read.
    myRevenueByTime: (params?: RevenueByTimeParams) =>
        api.get<RevenueByTimeEntry[]>('/sales-orders/revenue/by-time', {
            params: params as Record<string, string | undefined>,
        }),

    // Self-scoped order counts by status for the authenticated sales rep.
    // Gated sales_orders.read — does not require reports.read.
    myOrdersByStatus: (params?: MyDeliverySummaryParams) =>
        api.get<MyOrdersByStatusResponse>(
            '/sales-orders/summary/mine/by-status',
            {
                params: params as Record<string, string | undefined>,
            },
        ),

    // Self-scoped daily delivery counts for the authenticated driver.
    // Gated sales_orders.read — does not require reports.read.
    myDeliveriesByTime: (params?: MyDeliverySummaryParams) =>
        api.get<DeliveryByTimeEntry[]>('/sales-orders/deliveries/by-time', {
            params: params as Record<string, string | undefined>,
        }),
}

export interface MyRevenueSummary {
    total_revenue: number
    total_orders: number
    average_order_value: number
    paid_amount: number
    unpaid_amount: number
    unique_customers: number
    date_from: string
    date_to: string
}

export interface RevenueByTimeParams {
    group_by?: 'day' | 'week' | 'month' | 'year'
    date_from?: string
    date_to?: string
}

export interface RevenueByTimeEntry {
    period: string
    revenue: number
    order_count: number
}

export interface MyOrdersByStatusResponse {
    total_orders: number
    by_status: Record<string, number>
    delivery_rate: number
}

export interface DeliveryByTimeEntry {
    period: string
    count: number
}

export interface ListStockOutNotesParams {
    status?: StockOutNoteStatus
    page?: number
    per_page?: number
}

export const stockOutNoteApi = {
    /**
     * List stock-out notes with optional status filter + pagination.
     * Gated stock_out.confirm.
     */
    list: (params?: ListStockOutNotesParams) =>
        // Cast matches the pre-existing pattern for typed params in this codebase.
        api.get<StockOutNote[]>('/stock-out-notes', {
            params: params as Record<
                string,
                string | number | boolean | undefined
            >,
        }),
    /**
     * Resolve a PXK-... code (typed or scanned from packing slip) to its
     * stock-out note so the warehouse confirm screen can load by URL code.
     */
    getByCode: (code: string) =>
        api.get<StockOutNote>(
            `/stock-out-notes/by-code/${encodeURIComponent(code)}`,
        ),
    confirm: (id: string, data: ConfirmStockOutNoteRequest) =>
        api.put<StockOutNote>(`/stock-out-notes/${id}/confirm`, data),
}
